/**
 * Data layer for `payout-record-pull`. Thin typed fetch wrappers over /api/v1/payout-record-pull.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PayoutRecordPullRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPayoutRecordPullResponse {
	count: number;
	items: PayoutRecordPullRow[];
}

export interface CreatePayoutRecordPullBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/payout-record-pull";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPayoutRecordPullResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPayoutRecordPullResponse>(res);
}

export async function create(body: CreatePayoutRecordPullBody): Promise<PayoutRecordPullRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PayoutRecordPullRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const payoutRecordPullQueryKeys = {
	all: ["payout-record-pull"] as const,
	list: () => [...payoutRecordPullQueryKeys.all] as const,
	detail: (id: string) => [...payoutRecordPullQueryKeys.all, id] as const,
};
